import React, { Fragment } from 'react';
import { Button, } from 'reactstrap';
import { keyBy, last, sortBy, omit, uniq } from 'lodash';
import { toast } from 'react-toastify';
import { useToggle } from 'react-use';
import { Container, Draggable } from 'react-smooth-dnd';
import arrayMove from 'array-move';

import firebase from '../../firebase';
import { batch } from '../../shared/firebase';
import { mapKeysToJa, mapKeysFromJa, } from '../../shared/texts';
import { parents as sectionParents, } from '../../shared/models/customSection';
import { canEditCustomSections } from '../../shared/models/company';
import { canWriteCustomSections } from '../../abilities';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import HelpLink from '../HelpLink';
import EditButton from '../EditButton';
import DeleteButton from '../DeleteButton';
import UserDisplay from '../UserDisplay';
import ShareButton from '../ShareButton';
import CustomSectionFormModal from '../modals/CustomSectionFormModal';
import CompanyPage from '../hocs/CompanyPage';

const db = firebase.firestore();
const companiesRef = db.collection('companies');

export default CompanyPage(CompanyCustomSections);

function CompanyCustomSections(props) {
  const { company, user, members, match: { params: { companyId } } } = props;
  const canSort = canWriteCustomSections(user, companyId);
  const canEdit = canSort && canEditCustomSections(company);
  const [showsModal, toggleModal] = useToggle();
  const customSections = useCollectionSubscription(companiesRef.doc(companyId).collection('customSections').orderBy('createdAt'), [companyId]);
  const sortedItems = sortBy(customSections, _ => _.index != null ? _.index : Number.MAX_SAFE_INTEGER, _ => _.createdAt?.toMillis?.() ?? 0, _ => _.id);
  const freeeSections = [
    { id: 0, name: '未選択' },
    ...useCollectionSubscription(companiesRef.doc(companyId).collection('sections'), [companyId]),
  ];
  const parentFreeeSectionIds = freeeSections.map(_ => _.parent_id).filter(_ => _ != null);
  const tipFreeeSections = freeeSections
    .filter(_ => !parentFreeeSectionIds.includes(_.id))
    .map(_ => ({ ..._, parents: sectionParents(_, freeeSections) }));
  const onSubmit = async (values) => {
    const ref = companiesRef.doc(companyId).collection('customSections').doc();
    try {
      await ref.set({ ...omit(values, 'id'), createdAt: new Date(), index: ((last(sortedItems) || {}).index + 1 || sortedItems.length) });
      toast.success('保存しました');
      toggleModal(false);
    } catch(e) {
      console.error(e);
      toast.error('失敗しました');
    }
  };
  const onDrop = async ({ addedIndex, removedIndex }) => {
    const newIds = arrayMove(sortedItems, removedIndex, addedIndex).map(_ => _.id);
    await batch(db, newIds, (batch, id, index) => {
      batch.update(company.ref.collection('customSections').doc(id), { index });
    });
  };

  return (
    <div className="company-custom-sections">
      <div className="container py-5">
        <div className="d-flex justify-content-end mb-3">
          {canEdit && <HelpLink text="カスタム部門を作成する" />}
        </div>
        <div className="d-flex justify-content-center mb-2">
          <h3>カスタム部門</h3>
        </div>
        <div className="d-flex justify-content-end gap-1">
          {
            canEdit && (
              <Fragment>
                <BatchButton {...{ company, freeeSections: tipFreeeSections, customSections, }} />
                <Button color="primary" onClick={_ => toggleModal(true)}>
                  <span className="fas fa-plus mr-1" />
                  追加
                </Button>
              </Fragment>
            )
          }
        </div>
        {
          sortedItems.length > 0 ? (
            <Container
              dragHandleSelector=".drag-handle"
              onDrop={onDrop}
              dropPlaceholder={{ className: 'bg-light', }}
              render={(ref) => (
                <div ref={ref} className="border rounded mt-2">
                  {
                    sortedItems.map((customSection) => {
                      const { id, name, sectionIds, } = customSection;
                      const itemRef = companiesRef.doc(companyId).collection('customSections').doc(id);
                      return (
                        <Draggable
                          key={id}
                          render={() => (
                            <div>
                              <div className="d-flex align-items-center border-bottom px-2 py-2 bg-white">
                                {
                                  canSort && (
                                    <div className="drag-handle text-muted cursor-pointer px-2">
                                      <span className="fas fa-grip-vertical" />
                                    </div>
                                  )
                                }
                                <div className="flex-grow-1">
                                  {name}
                                  <div className="mt-1">
                                    {
                                      sectionIds?.map((sectionId) => {
                                        const section = freeeSections.find(_ => _.id === sectionId);
                                        return section && (
                                          <span key={sectionId} className="badge badge-secondary mr-1">
                                            {[...(sectionParents(section, freeeSections).map(_ => _.name)), section.name].join(' - ')}
                                          </span>
                                        );
                                      })
                                    }
                                  </div>
                                </div>
                                {
                                  canEdit && (
                                    <div className="d-flex gap-1 justify-content-end align-items-start text-nowrap">
                                      <ShareButton showsExpires label="共有" screenType="monthlyBudgets" {...{ company, subjectType: 'section', subjects: [customSection], }} />
                                      <EditButton itemRef={itemRef} FormModal={CustomSectionFormModal} formProps={{ freeeSections: tipFreeeSections, customSections: customSections.filter(_ => _ !== customSection), companyMembers: members, }} />
                                      <DeleteButton itemRef={itemRef} />
                                    </div>
                                  )
                                }
                              </div>
                            </div>
                          )}
                        />
                      );
                    })
                  }
                </div>
              )}
            />
          ) : (
            <span>カスタム部門は未登録です</span>
          )
        }
      </div>
      <CustomSectionFormModal isOpen={showsModal} onClickClose={_ => toggleModal(false)} onSubmit={onSubmit} freeeSections={tipFreeeSections} customSections={customSections} companyMembers={members} />
    </div>
  );
}

function BatchButton (props) {
  const { customSections, freeeSections, company, } = props;
  const usedFreeeSectionIds = customSections.flatMap(_ => _.sectionIds || []);
  const existingNames = customSections.map(_ => _.name);
  const [isProcessing, toggleProcessing] = useToggle(false);
  const onClick = async () => {
    if(!window.confirm('freeeと同名のカスタム部門を一括登録します。よろしいですか？')) return;

    const unusedFreeeSections = freeeSections.filter(_ => !usedFreeeSectionIds.includes(_.id));
    // NOTE: 既存カスタム部門と同名のものは重複作成しない（取り込まずスキップ）
    const targetFreeeSections = unusedFreeeSections.filter(_ => !existingNames.includes(_.name));
    const skippedSectionNames = uniq(unusedFreeeSections.filter(_ => existingNames.includes(_.name)).map(_ => _.name));

    toggleProcessing(true);
    try {
      await batch(db, targetFreeeSections, (batch, freeeSection) => {
        batch.set(company.ref.collection('customSections').doc(), {
          name: freeeSection.name,
          sectionIds: [freeeSection.id],
          createdAt: new Date(),
        });
      });
      toast.success('一括登録しました');
      if(skippedSectionNames.length > 0) {
        toast.warn(`既存のカスタム部門と同名のため、次の部門は取り込みませんでした：${skippedSectionNames.join('、')}`, { autoClose: false });
      }
    } catch(e) {
      toast.error('失敗しました');
      throw e;
    }
    toggleProcessing(false);
  };

  return (
    <Button disabled={isProcessing} onClick={onClick}>
      {isProcessing && <span className="fas fa-spin fa-spinner" />}
      freeeと同名のカスタム部門を一括登録する
    </Button>
  );
}
